import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PatchEducationHistoryCoursesRequestBody,
  PatchEducationHistoryCoursesResponseBody,
  CourseUpdate,
} from '@ab/ams-school-api/types/private/v1';
import { config } from '~/config';
import { hasKey } from '~/utils';
import { useRequest } from '~/lib/useRequest';

export type UpdateEducationHistoryCoursesParams = {
  studentId: string;
  educationHistoryId: string;
  courses: CourseUpdate[];
  applicationId: string;
};

export function useUpdateEducationHistoryCourses() {
  const request = useRequest();
  const queryClient = useQueryClient();

  const { isLoading, error, mutateAsync } = useMutation({
    mutationKey: ['updateEducationHistoryCourses'],
    mutationFn: async (params: UpdateEducationHistoryCoursesParams) => {
      const response = await request<PatchEducationHistoryCoursesResponseBody>(
        `${config.apiHost}/private/v1/students/${params.studentId}/education-history/${params.educationHistoryId}`,
        {
          method: 'PATCH',
          body: {
            data: {
              type: 'education-history',
              attributes: {
                courses: params.courses,
              },
            },
          } as PatchEducationHistoryCoursesRequestBody,
          isExpectedResponse: isPatchExpectedResponse,
        }
      );

      return response.data;
    },
    onSuccess: async (_data, variables) => {
      // Invalidate application and entity history queries in parallel
      // so both the education history data and "Updated on" timestamps refresh
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['application', variables.applicationId] }),
        queryClient.invalidateQueries({
          queryKey: ['entity-history', 'educationHistory', variables.educationHistoryId],
        }),
      ]);
    },
  });

  return {
    updateEducationHistoryCourses: mutateAsync,
    isUpdatingEducationHistoryCourses: isLoading,
    updateEducationHistoryCoursesError: error,
  };
}

function isPatchExpectedResponse(res: unknown): res is PatchEducationHistoryCoursesResponseBody {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}
